/**
 * DeleteCustomerDialog Component
 * Confirmation dialog for deleting a customer
 *
 * SCHEMA MIGRATION: Simplified - no force delete needed
 * Migration Date: 2025-11-03
 * - Vehicles are automatically cleaned up (customer_id removed)
 * - No confirmation or force flag required
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import type { Customer } from '../../../types/customer';

interface DeleteCustomerDialogProps {
  open: boolean;
  customer: Customer | null;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const DeleteCustomerDialog: React.FC<DeleteCustomerDialogProps> = ({
  open,
  customer,
  isDeleting,
  onConfirm,
  onCancel,
}) => {
  return (
    <Dialog
      open={open}
      onClose={isDeleting ? undefined : onCancel}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle id="delete-dialog-title" sx={{ pb: 1 }}>
        Delete Customer
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="delete-dialog-description" sx={{ mb: 2 }}>
          Are you sure you want to delete <strong>{customer?.name}</strong>?
          {customer?.email && <> ({customer.email})</>}
        </DialogContentText>

        {/* Info message about automatic cleanup */}
        <Alert severity="info" sx={{ mt: 2 }}>
          Customer will be deleted and all associated vehicles will be unassigned (no owner).
          This action cannot be undone.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onCancel}
          disabled={isDeleting}
          sx={{
            color: '#6b7280',
            '&:hover': {
              backgroundColor: '#f3f4f6',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          sx={{
            backgroundColor: '#ef4444',
            '&:hover': {
              backgroundColor: '#dc2626',
            },
            '&:disabled': {
              backgroundColor: '#fca5a5',
              color: 'white',
            },
          }}
          autoFocus
        >
          {isDeleting ? 'Deleting...' : 'Delete Customer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteCustomerDialog;
